import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import CarCard from '../components/CarCard';
import ComparisonView from '../components/ComparisonView';
import { useLanguage } from '../context/LanguageContext';
import { useComparison } from '../context/ComparisonContext';
import { t } from '../translations/translations';
import SEO from '../components/SEO';
import { api } from '../api';
import { useTheme } from '../context/ThemeContext';

const SEASONS = ['all', 'summer', 'winter'];
const WEATHERS = ['all', 'sun', 'rain', 'snow', 'cloudy', 'night'];
const SORTS = ['range', 'charging', 'temperature', 'date'];
const WEATHER_EMOJI = { sun: '☀️ ', rain: '🌧️ ', snow: '❄️ ', cloudy: '☁️ ', night: '🌙 ' };

function ChargingStopTest() {
  const { darkMode } = useTheme();
  const { language } = useLanguage();
  const { comparedCars, toggleComparison, isInComparison, addToComparison, removeFromComparison } = useComparison();
  const [carsData, setCarsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  // URL is the single source of truth for search, filters, sort and view,
  // so refresh/back/shared links keep the state.
  const [searchParams, setSearchParams] = useSearchParams();
  const searchQuery = searchParams.get('q') || '';
  const filterSeason = SEASONS.includes(searchParams.get('sesong')) ? searchParams.get('sesong') : 'all';
  const filterWeather = WEATHERS.includes(searchParams.get('vaer')) ? searchParams.get('vaer') : 'all';
  const sortBy = SORTS.includes(searchParams.get('sortering')) ? searchParams.get('sortering') : 'range';
  const view = searchParams.get('visning') === 'tabell' ? 'tabell' : 'kort';

  const setParam = (key, value, defaultValue, replace = false) => {
    const next = new URLSearchParams(searchParams);
    if (!value || value === defaultValue) {
      next.delete(key);
    } else {
      next.set(key, value);
    }
    setSearchParams(next, { replace });
  };

  const resetFilters = () => setSearchParams({}, { replace: true });

  useEffect(() => {
    api('/api/cars').then(res => {
      if (!res.ok) throw new Error('bad response');
      return res.json();
    }).then(data => {
      setCarsData(data);
      setLoading(false);
    }).catch(() => {
      setLoadError(true);
      setLoading(false);
    });
  }, []);

  const filteredAndSortedCars = [...carsData]
    .filter((car) => {
      const matchesWeather = filterWeather === 'all' || car.weather === filterWeather;
      const matchesSeason = filterSeason === 'all' || car.season === filterSeason;
      const matchesSearch = searchQuery === '' ||
        car.make.toLowerCase().includes(searchQuery.toLowerCase()) ||
        car.model.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesWeather && matchesSeason && matchesSearch;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'range':
          return b.rangeKm - a.rangeKm;
        case 'charging':
          return b.maxChargingSpeed - a.maxChargingSpeed;
        case 'temperature':
          return b.temperature - a.temperature;
        case 'date':
          return new Date(b.testDate) - new Date(a.testDate);
        default:
          return 0;
      }
    });

  // theme-aware styles so the page reads correctly in light mode too
  const activeBtn = 'bg-teal-500 text-slate-900 shadow-lg shadow-teal-500/50';
  const inactiveBtn = darkMode
    ? 'text-gray-300 hover:bg-slate-700 hover:text-teal-400'
    : 'text-stone-600 hover:bg-stone-100 hover:text-teal-600';
  const pillBox = darkMode
    ? 'bg-slate-800 bg-opacity-60 backdrop-blur-md border border-teal-500 border-opacity-30 shadow-xl'
    : 'bg-white border border-stone-200 shadow';
  const selectStyle = darkMode
    ? 'bg-slate-800 border border-teal-500 border-opacity-30 text-gray-100'
    : 'bg-white border border-stone-300 text-stone-900';

  const sortOptions = [
    ['range', t(language, 'rangeTest.sortByRange')],
    ['charging', t(language, 'rangeTest.sortByCharging')],
    ['temperature', t(language, 'rangeTest.sortByTemp')],
    ['date', t(language, 'rangeTest.sortByNewest')],
  ];

  const pillButton = (value, current, onPick, label, bold = false) => (
    <button
      key={value}
      onClick={() => onPick(value)}
      className={`px-4 py-2 rounded-lg ${bold ? 'font-bold px-5' : 'font-semibold'} transition-all duration-200 text-sm ${
        current === value ? activeBtn : inactiveBtn
      }`}
    >
      {label}
    </button>
  );

  return (
    <div className="min-h-screen pt-32 pb-12 px-4 sm:px-6 lg:px-8">
      <SEO
        title="Rekkeviddetest elbil"
        path="/ladestopp"
        description={`Sammenlign rekkevidde på ${carsData.length} elbiler testet i norske forhold. Filtrer på vær, sesong og sorter etter rekkevidde. Oppdaterte tester med WLTP-sammenligning.`}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: 'Elbil rekkeviddetester',
          description: 'Grundige rekkeviddetester av elbiler i norske forhold',
          url: 'https://effektnorge.no/ladestopp',
          inLanguage: 'nb-NO',
          mainEntity: {
            '@type': 'ItemList',
            numberOfItems: carsData.length,
            itemListElement: carsData.map((car, i) => ({
              '@type': 'ListItem',
              position: i + 1,
              item: {
                // Not Product: we have no price/review/rating, and Google flags
                // Product snippets without offers/review/aggregateRating.
                '@type': 'Thing',
                name: `${car.make} ${car.model} ${car.year}`,
                description: `Rekkeviddetest: ${car.rangeKm} km (WLTP: ${car.wltpRange} km, batteri: ${car.batterySize} kWh). Testet i ${car.weather === 'sun' ? 'sol' : car.weather === 'rain' ? 'regn' : car.weather === 'snow' ? 'snø' : car.weather === 'night' ? 'natt' : 'overskyet'} ved ${car.temperature}°C.`
              }
            }))
          }
        }}
      />
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className={`text-4xl sm:text-5xl font-black mb-4 ${darkMode ? 'text-white drop-shadow-lg' : 'text-stone-900'}`}>
            {t(language, 'rangeTest.title')}
          </h1>
          <p className={`text-xl font-light mb-4 ${darkMode ? 'text-gray-300' : 'text-stone-600'}`}>
            {t(language, 'rangeTest.subtitle')}
          </p>

          {/* Methodology strip — the premise of the test, visible on all devices */}
          <div className={`max-w-3xl mx-auto mb-6 px-5 py-3 rounded-xl text-sm sm:text-base border ${
            darkMode
              ? 'bg-slate-800 bg-opacity-60 border-teal-500 border-opacity-30 text-gray-200'
              : 'bg-teal-50 border-teal-200 text-stone-700'
          }`}>
            <span className={darkMode ? 'text-teal-400' : 'text-teal-700'} aria-hidden="true">⚡ </span>
            {t(language, 'rangeTest.method')}
          </div>

          {/* Compact Filters Section */}
          <div className="max-w-5xl mx-auto space-y-3">
            {/* Search Field */}
            <div className="w-full">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setParam('q', e.target.value, '', true)}
                placeholder={t(language, 'rangeTest.searchPlaceholder')}
                className={`w-full px-6 py-3 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent shadow-xl ${
                  darkMode
                    ? 'bg-slate-800 bg-opacity-60 backdrop-blur-md border border-teal-500 border-opacity-30 text-gray-100 placeholder-gray-400'
                    : 'bg-white border border-stone-300 text-stone-900 placeholder-stone-500'
                }`}
              />
            </div>

            {/* Mobile: compact dropdowns */}
            <div className="sm:hidden grid grid-cols-1 gap-2">
              <select
                value={filterSeason}
                onChange={(e) => setParam('sesong', e.target.value, 'all')}
                aria-label={t(language, 'rangeTest.seasonLabel')}
                className={`w-full px-4 py-3 rounded-xl font-semibold text-sm ${selectStyle}`}
              >
                <option value="all">{t(language, 'rangeTest.allSeasons')}</option>
                <option value="summer">{t(language, 'rangeTest.summerTests')}</option>
                <option value="winter">{t(language, 'rangeTest.winterTests')}</option>
              </select>
              <select
                value={filterWeather}
                onChange={(e) => setParam('vaer', e.target.value, 'all')}
                aria-label={t(language, 'rangeTest.weatherLabel')}
                className={`w-full px-4 py-3 rounded-xl font-semibold text-sm ${selectStyle}`}
              >
                <option value="all">{t(language, 'rangeTest.allWeather')}</option>
                {WEATHERS.slice(1).map((w) => (
                  <option key={w} value={w}>{WEATHER_EMOJI[w]}{t(language, `rangeTest.${w}`)}</option>
                ))}
              </select>
              <select
                value={sortBy}
                onChange={(e) => setParam('sortering', e.target.value, 'range')}
                aria-label={t(language, 'rangeTest.sortLabel')}
                className={`w-full px-4 py-3 rounded-xl font-semibold text-sm ${selectStyle}`}
              >
                {sortOptions.map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>

            {/* Desktop: pill groups */}
            <div className="hidden sm:flex flex-wrap items-center justify-center gap-3">
              {/* Season Filter */}
              <div className={`inline-flex rounded-xl p-2 ${pillBox}`}>
                {pillButton('all', filterSeason, (v) => setParam('sesong', v, 'all'), t(language, 'rangeTest.allSeasons'), true)}
                {pillButton('summer', filterSeason, (v) => setParam('sesong', v, 'all'), t(language, 'rangeTest.summerTests'), true)}
                {pillButton('winter', filterSeason, (v) => setParam('sesong', v, 'all'), t(language, 'rangeTest.winterTests'), true)}
              </div>

              {/* Weather Filter */}
              <div className={`inline-flex rounded-xl p-2 ${pillBox}`}>
                {pillButton('all', filterWeather, (v) => setParam('vaer', v, 'all'), t(language, 'rangeTest.allWeather'))}
                {WEATHERS.slice(1).map((w) =>
                  pillButton(w, filterWeather, (v) => setParam('vaer', v, 'all'), `${WEATHER_EMOJI[w]}${t(language, `rangeTest.${w}`)}`)
                )}
              </div>

              {/* Sort Controls */}
              <div className={`inline-flex rounded-xl p-2 ${pillBox}`}>
                {sortOptions.map(([value, label]) =>
                  pillButton(value, sortBy, (v) => setParam('sortering', v, 'range'), label)
                )}
              </div>
            </div>

            {/* View toggle */}
            <div className="flex justify-center">
              <div className={`inline-flex rounded-xl p-2 ${pillBox}`}>
                {pillButton('kort', view, (v) => setParam('visning', v, 'kort'), t(language, 'rangeTest.viewCards'))}
                {pillButton('tabell', view, (v) => setParam('visning', v, 'kort'), t(language, 'rangeTest.viewTable'))}
              </div>
            </div>
          </div>
        </div>

        {/* Loading skeleton */}
        {loading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 animate-pulse">
            {[0, 1, 2].map((i) => (
              <div key={i} className={`rounded-2xl h-[500px] ${darkMode ? 'bg-slate-800' : 'bg-stone-200'}`} />
            ))}
          </div>
        )}

        {/* Load error */}
        {!loading && loadError && (
          <div className={`text-center py-16 ${darkMode ? 'text-gray-300' : 'text-stone-600'}`}>
            <p className="text-lg font-medium">{t(language, 'rangeTest.loadError')}</p>
          </div>
        )}

        {!loading && !loadError && (
          <>
            {/* Stats Summary */}
            <div className={`mt-8 mb-4 text-center ${darkMode ? 'text-gray-300' : 'text-stone-600'}`}>
              <p className="text-lg font-medium">
                {t(language, 'rangeTest.showing')} {filteredAndSortedCars.length} {filteredAndSortedCars.length !== 1 ? t(language, 'rangeTest.testResults') : t(language, 'rangeTest.testResult')}
              </p>
            </div>

            {/* Empty state */}
            {filteredAndSortedCars.length === 0 && (
              <div className={`text-center py-12 ${darkMode ? 'text-gray-300' : 'text-stone-600'}`}>
                <p className="text-lg mb-4">{t(language, 'rangeTest.noResults')}</p>
                <button
                  onClick={resetFilters}
                  className="px-6 py-3 bg-teal-500 hover:bg-teal-400 text-slate-900 font-bold rounded-xl transition-colors"
                >
                  {t(language, 'rangeTest.resetFilters')}
                </button>
              </div>
            )}

            {/* Cards Grid */}
            {view === 'kort' && filteredAndSortedCars.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
                {filteredAndSortedCars.map((car) => (
                  <CarCard key={car.id} car={car} />
                ))}
              </div>
            )}

            {/* Table view */}
            {view === 'tabell' && filteredAndSortedCars.length > 0 && (
              <div className={`rounded-2xl border overflow-x-auto ${
                darkMode ? 'bg-slate-800 bg-opacity-60 border-teal-500 border-opacity-30' : 'bg-white border-stone-200 shadow'
              }`}>
                <table className="w-full text-sm whitespace-nowrap">
                  <thead>
                    <tr className={`text-left text-xs uppercase tracking-wide ${darkMode ? 'text-gray-400 border-b border-slate-700' : 'text-stone-500 border-b border-stone-200'}`}>
                      <th className="px-4 py-3 font-semibold">{t(language, 'rangeTest.tableCar')}</th>
                      {[
                        ['range', `${t(language, 'rangeTest.tableRange')} (km)`],
                        ['charging', `${t(language, 'rangeTest.tableCharging')} (kW)`],
                      ].map(([key, label]) => (
                        <th key={key} className="px-4 py-3 font-semibold">
                          <button
                            onClick={() => setParam('sortering', key, 'range')}
                            className={`uppercase tracking-wide hover:text-teal-500 ${sortBy === key ? 'text-teal-500' : ''}`}
                          >
                            {label}{sortBy === key ? ' ↓' : ''}
                          </button>
                        </th>
                      ))}
                      <th className="px-4 py-3 font-semibold">{t(language, 'card.battery')} (kWh)</th>
                      <th className="px-4 py-3 font-semibold">
                        <button
                          onClick={() => setParam('sortering', 'temperature', 'range')}
                          className={`uppercase tracking-wide hover:text-teal-500 ${sortBy === 'temperature' ? 'text-teal-500' : ''}`}
                        >
                          Temp{sortBy === 'temperature' ? ' ↓' : ''}
                        </button>
                      </th>
                      <th className="px-4 py-3 font-semibold">{t(language, 'rangeTest.seasonLabel')}</th>
                      <th className="px-4 py-3 font-semibold">
                        <button
                          onClick={() => setParam('sortering', 'date', 'range')}
                          className={`uppercase tracking-wide hover:text-teal-500 ${sortBy === 'date' ? 'text-teal-500' : ''}`}
                        >
                          {t(language, 'rangeTest.tableDate')}{sortBy === 'date' ? ' ↓' : ''}
                        </button>
                      </th>
                      <th className="px-4 py-3 font-semibold">{t(language, 'rangeTest.tableVideo')}</th>
                      <th className="px-4 py-3 font-semibold"></th>
                    </tr>
                  </thead>
                  <tbody className={darkMode ? 'text-gray-100' : 'text-stone-900'}>
                    {filteredAndSortedCars.map((car) => (
                      <tr key={car.id} className={darkMode ? 'border-b border-slate-700 last:border-0' : 'border-b border-stone-100 last:border-0'}>
                        <td className="px-4 py-3">
                          <span className="font-bold">{car.make} {car.model}</span>
                          <span className={`ml-2 ${darkMode ? 'text-gray-400' : 'text-stone-500'}`}>{car.year}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="font-bold">{car.rangeKm}</span>
                        </td>
                        <td className="px-4 py-3">{car.maxChargingSpeed}</td>
                        <td className="px-4 py-3">{car.batterySize}{car.batteryChemistry ? ` (${car.batteryChemistry})` : ''}</td>
                        <td className="px-4 py-3">{car.temperature}°C</td>
                        <td className="px-4 py-3">
                          {WEATHER_EMOJI[car.weather] || ''}{car.season === 'winter' ? t(language, 'card.winter') : t(language, 'card.summer')}
                        </td>
                        <td className="px-4 py-3">{car.testDate}</td>
                        <td className="px-4 py-3">
                          {car.youtubeUrl && (
                            <a href={car.youtubeUrl} target="_blank" rel="noopener noreferrer" className="text-red-500 hover:text-red-400 font-semibold">
                              YouTube
                            </a>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => isInComparison(car.id) ? removeFromComparison(car.id) : addToComparison(car)}
                            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                              isInComparison(car.id)
                                ? 'bg-teal-500 text-slate-900'
                                : darkMode ? 'bg-slate-700 text-gray-200 hover:bg-slate-600' : 'bg-stone-100 text-stone-700 hover:bg-stone-200'
                            }`}
                          >
                            {t(language, isInComparison(car.id) ? 'card.comparing' : 'card.compare')}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Footer */}
            <div className={`mt-16 text-center ${darkMode ? 'text-gray-400' : 'text-stone-500'}`}>
              <p className="text-sm">
                {t(language, 'rangeTest.disclaimer')}
              </p>
            </div>
          </>
        )}
      </div>

      {/* Floating Comparison Button */}
      {comparedCars.length > 0 && (
        <button
          onClick={toggleComparison}
          className="fixed bottom-8 right-8 z-50 flex items-center gap-3 px-6 py-4 bg-teal-500 hover:bg-teal-400 text-slate-900 font-bold rounded-full shadow-2xl transition-all duration-200 hover:scale-110"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
          {t(language, 'card.viewComparison')} ({comparedCars.length})
        </button>
      )}

      {/* Comparison View */}
      <ComparisonView />
    </div>
  );
}

export default ChargingStopTest;
